import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, LinearProgress,
  useMediaQuery, Avatar, InputBase, Button, MenuItem,
  Select, Paper, List, ListItem, ListItemText, ClickAwayListener,
} from '@mui/material';
import { useTheme, styled, keyframes } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { useFormik, FormikProvider, Form } from 'formik';
import * as Yup from 'yup';
import { CloudUpload, KeyboardArrowDown, KeyboardArrowUp, Close, Check, ExpandMore } from '@mui/icons-material';
import { FetchApplyFormInfo, SubmitApplyForm, SendApplyFormOtp, VerifyApplyFormOtp } from 'api/apply_form';
import {
  FetchAllCountries, FetchAllCountriesWithPhoneCodes,
  FetchAllStates, FetchAllCities,
  FetchAllStages, FetchAllSectors, FetchAllCategories, FetchAllBusinessModel,
  FetchAllRoundTypes, FetchAllCurrencyLists,
  FetchAllSubCategories,
} from 'api/utils';
import { sanitizePersonName, currencyInputProps } from 'utils/index';
import { capitalizeFirst } from 'views/onboarding-v2/steps/BasicDetails';

const toOpts = (arr) =>
  (arr || []).map((item) => ({
    label: item.name || item.label || item?.sub_category || '',
    value: item._id || item.value || '',
    _id: item._id || '',
  }));

// ── Animations ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const progressFill = keyframes`
  from { width: 0%; } to { width: 100%; }
`;

// ── Design tokens (Typeform palette) ─────────────────────────────────────────
const TF_BG = '#ffffff';
const TF_TEXT = '#262627';       // near-black labels
const TF_SUBTEXT = '#8a8a8a';       // placeholders / captions
const TF_BORDER = '#d3d3d3';       // underline default
const TF_ACTIVE = '#1a1a2e';       // underline focused / selected
const TF_BTN = '#2d3748';       // dark slate button
const TF_BTN_HOVER = '#1a202c';
const TF_HEADER_BG = '#f4f4f4';       // step header strip
const TF_PROGRESS = '#2d3748';       // progress bar fill

// ── Typeform underline input ──────────────────────────────────────────────────
const TFInput = styled(InputBase)(({ error }) => ({
  width: '100%',
  fontSize: '1.05rem',
  color: TF_TEXT,
  paddingBottom: 6,
  borderBottom: `2px solid ${error ? '#e53e3e' : TF_BORDER}`,
  transition: 'border-color 0.2s',
  '& input, & textarea': {
    padding: 0,
    '&::placeholder': { color: TF_SUBTEXT, opacity: 1 },
  },
  '&.Mui-focused': { borderBottomColor: TF_ACTIVE },
}));

// ── Typeform label ────────────────────────────────────────────────────────────
const TFLabel = ({ children }) => (
  <Typography sx={{ fontSize: '0.92rem', fontWeight: 500, color: TF_TEXT, mb: 1, fontFamily: 'inherit' }}>
    {children}
  </Typography>
);

// ── Error text ────────────────────────────────────────────────────────────────
const ErrText = ({ msg }) =>
  msg ? <Typography sx={{ color: '#e53e3e', fontSize: '0.78rem', mt: 0.5 }} data-error="true"  >{msg}</Typography> : null;

// ── Reusable scroll helper ────────────────────────────────────────────────────
const scrollToFirstError = () => {
  // Small delay to let React render the ErrText nodes first
  setTimeout(() => {
    const firstError = document.querySelector('[data-error="true"]');

    console.log(firstError, "firstError");
    if (firstError) {
      firstError.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, 50);
};

// ── Reusable scroll to top helper ────────────────────────────────────────────
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// ── Typeform dark button ──────────────────────────────────────────────────────
const TFButton = styled(Button)(() => ({
  backgroundColor: TF_BTN,
  color: '#fff',
  borderRadius: 4,
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '0.95rem',
  padding: '10px 22px',
  minWidth: 72,
  boxShadow: 'none',
  '&:hover': { backgroundColor: TF_BTN_HOVER, boxShadow: 'none' },
  '&:disabled': { backgroundColor: '#a0aec0', color: '#fff' },
}));

// ── "press Enter ↵" hint ──────────────────────────────────────────────────────
const EnterHint = () => (
  <Typography component="span" sx={{ display: { xs: "none", md: "block" }, ml: 1.5, fontSize: '0.8rem', color: TF_SUBTEXT, verticalAlign: 'middle' }}>
    press Enter <Box component="span" sx={{ fontWeight: 600 }}>↵</Box>
  </Typography>
);

// ── InitialsAvatar ────────────────────────────────────────────────────────────
const InitialsAvatar = ({ name, size = 56 }) => {
  const initials = name
    ? name.split(' ').filter(Boolean).map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: '#e05c28', fontSize: size * 0.38, fontWeight: 700, borderRadius: 2 }}>
      {initials}
    </Avatar>
  );
};

function TFDropdown({ label, placeholder, options, value, onSelect, error, errorMsg, multiple = false, autoFocus = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropUp, setDropUp] = useState(false);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!autoFocus) return;
    const t = setTimeout(() => {
      recalcPosition();
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }, 150); // slight delay so the step animation finishes first
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const DROPDOWN_HEIGHT = 220;
  const OFFSET = 6;

  // Normalise: single mode → object|null, multi mode → array
  const selectedArr = multiple ? (Array.isArray(value) ? value : []) : null;

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const recalcPosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setDropUp(spaceBelow < DROPDOWN_HEIGHT + OFFSET && spaceAbove > spaceBelow);
  }, []);

  useEffect(() => {
    if (!open) return;
    recalcPosition();
    window.addEventListener('scroll', recalcPosition, true);
    window.addEventListener('resize', recalcPosition);
    return () => {
      window.removeEventListener('scroll', recalcPosition, true);
      window.removeEventListener('resize', recalcPosition);
    };
  }, [open, recalcPosition]);

  const handleOpen = () => {
    recalcPosition();
    setOpen((o) => !o);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelect = (opt) => {
    if (multiple) {
      const already = selectedArr.some((s) => s._id === opt._id);
      const next = already
        ? selectedArr.filter((s) => s._id !== opt._id)
        : [...selectedArr, opt];
      onSelect(next);
      // keep open for multi-select
    } else {
      onSelect(opt);
      setOpen(false);
      setSearch('');
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(multiple ? [] : null);
    setSearch('');
  };

  const handleRemoveChip = (e, id) => {
    e.stopPropagation();
    onSelect(selectedArr.filter((s) => s._id !== id));
  };

  const paperPositionSx = dropUp
    ? { bottom: '100%', top: 'auto', mb: `${OFFSET}px`, mt: 0 }
    : { top: '100%', bottom: 'auto', mt: `${OFFSET}px`, mb: 0 };

  const paperRadiusSx = dropUp
    ? { borderRadius: '8px 8px 4px 4px' }
    : { borderRadius: '4px 4px 8px 8px' };

  const anchorBorderSx = open
    ? dropUp
      ? { borderBottomColor: TF_BORDER, borderTopColor: TF_ACTIVE }
      : { borderBottomColor: TF_ACTIVE }
    : { borderBottomColor: error ? '#e53e3e' : TF_BORDER };

  const hasValue = multiple ? selectedArr.length > 0 : !!value;

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); setSearch(''); }}>
      <Box sx={{ position: 'relative' }}>
        {label && <TFLabel>{label}</TFLabel>}

        {/* Anchor / trigger row */}
        <Box
          ref={anchorRef}
          onClick={handleOpen}
          sx={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,
            borderBottom: `2px solid ${error ? '#e53e3e' : open ? TF_ACTIVE : TF_BORDER}`,
            ...anchorBorderSx,
            pb: 0.75, cursor: 'text',
            transition: 'border-color 0.2s',
            minHeight: 36,
          }}
        >
          {/* Multi: show chips */}
          {multiple && selectedArr.map((s) => (
            <Box
              key={s._id}
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                bgcolor: '#f0f0f0', borderRadius: '4px', px: 0.8, py: 0.3,
                fontSize: '0.82rem', color: TF_TEXT, fontWeight: 500,
              }}
            >
              {s.label}
              <Close onClick={(e) => handleRemoveChip(e, s._id)} sx={{ fontSize: 13, cursor: 'pointer', color: TF_SUBTEXT }} />
            </Box>
          ))}

          {/* Search input when open, or placeholder text */}
          {open ? (
            <InputBase
              inputRef={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={multiple ? 'Search…' : (value?.label || placeholder)}
              sx={{
                flex: 1, minWidth: 80, fontSize: '1.05rem', color: TF_TEXT,
                '& input::placeholder': { color: TF_SUBTEXT, opacity: 1 },
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus={autoFocus}
            />
          ) : !multiple && (
            <Typography sx={{ flex: 1, fontSize: '1.05rem', color: value ? TF_TEXT : TF_SUBTEXT }}>
              {value ? value.label : placeholder}
            </Typography>
          )}

          {/* Placeholder when multi + empty + closed */}
          {multiple && !open && selectedArr.length === 0 && (
            <Typography sx={{ flex: 1, fontSize: '1.05rem', color: TF_SUBTEXT }}>{placeholder}</Typography>
          )}

          {hasValue && !open
            ? <Close onClick={handleClear} sx={{ fontSize: 18, color: TF_SUBTEXT, cursor: 'pointer', ml: 'auto' }} />
            : (
              <KeyboardArrowDown
                sx={{
                  fontSize: 20, color: TF_SUBTEXT, ml: 'auto',
                  transition: 'transform 0.2s',
                  transform: open ? (dropUp ? 'rotate(0deg)' : 'rotate(180deg)') : 'rotate(0deg)',
                }}
              />
            )}
        </Box>

        {/* Dropdown list */}
        {open && (
          <Paper
            ref={listRef}
            elevation={3}
            sx={{
              position: 'absolute',
              left: 0, right: 0,
              zIndex: 1300,
              maxHeight: DROPDOWN_HEIGHT,
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
              ...paperPositionSx,
              ...paperRadiusSx,
              animation: dropUp
                ? `${dropUpAnim} 0.18s ease both`
                : `${dropDownAnim} 0.18s ease both`,
            }}
          >
            {filtered.length === 0 ? (
              <Typography sx={{ p: 2, color: TF_SUBTEXT, fontSize: '0.9rem' }}>No options</Typography>
            ) : (
              filtered.map((opt) => {
                const isSelected = multiple
                  ? selectedArr.some((s) => s._id === opt._id)
                  : value?._id === opt._id;
                return (
                  <Box
                    key={opt._id || opt.value}
                    onClick={() => handleSelect(opt)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2, py: 1.2, cursor: 'pointer',
                      backgroundColor: isSelected ? '#f1f1f1' : 'transparent',
                      '&:hover': { backgroundColor: '#f7f7f7' },
                      borderBottom: '1px dashed #e8e8e8',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.95rem', color: TF_TEXT, fontWeight: isSelected ? 600 : 400 }}>
                      {opt.label}
                    </Typography>
                    {isSelected && <Check sx={{ fontSize: 16, color: TF_BTN }} />}
                  </Box>
                );
              })
            )}
          </Paper>
        )}

        <ErrText msg={errorMsg} />
      </Box>
    </ClickAwayListener>
  );
}

// ── Direction-aware entrance animations ───────────────────────────────────────
// Add these alongside your other keyframes at the top of the file

const dropDownAnim = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const dropUpAnim = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Phone field ───────────────────────────────────────────────────────────────
function PhoneField({ countryCodes, selectedCode, onCodeChange, value, onChange, onBlur, error, errorMsg }) {
  return (
    <Box>
      <TFLabel>Mobile Number *</TFLabel>
      <Box display="flex" alignItems="flex-end" gap={1.5} sx={{ borderBottom: `2px solid ${error ? '#e53e3e' : TF_BORDER}`, pb: 0.75 }}>
        <Select
          value={selectedCode}
          onChange={onCodeChange}
          variant="standard"
          disableUnderline
          IconComponent={ExpandMore}
          sx={{
            minWidth: 72, fontSize: '1rem', color: TF_TEXT,
            '& .MuiSelect-select': { pr: '22px !important', pb: 0 },
          }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}
          renderValue={(v) => (
            <Box display="flex" alignItems="center" gap={0.5}>
              {/* <Typography fontSize="1.1rem">🌐</Typography> */}
              <Typography fontSize="0.9rem" color={TF_TEXT}>+{v}</Typography>
              {/* <KeyboardArrowDown sx={{ fontSize: 16, color: TF_SUBTEXT }} /> */}
            </Box>
          )}
        >
          {(countryCodes || []).map((c) => (
            <MenuItem key={c._id || c.value} value={c.value} sx={{ fontSize: '0.875rem' }}>
              +{c.value} {c.label}
            </MenuItem>
          ))}
        </Select>
        <InputBase
          value={value} onChange={onChange} onBlur={onBlur}
          placeholder="Please enter mobile number"
          inputProps={currencyInputProps}
          sx={{ flex: 1, fontSize: '1.05rem', color: TF_TEXT, '& input::placeholder': { color: TF_SUBTEXT, opacity: 1 } }}
        />
      </Box>
      <ErrText msg={errorMsg} />
    </Box>
  );
}

// ── Field wrapper (adds spacing between fields) ───────────────────────────────
const FieldWrap = ({ children }) => (
  <Box sx={{ mb: 3.5 }}>{children}</Box>
);

// ── Step header strip ─────────────────────────────────────────────────────────
const StepHeader = ({ step, label }) => (
  <Box
    sx={{
      backgroundColor: TF_HEADER_BG,
      px: { xs: 2.5, md: 5 }, py: 1.5,
      display: 'flex', alignItems: 'center', gap: 1,
      borderBottom: '1px solid #e8e8e8',
      animation: `${fadeIn} 0.3s ease`,
    }}
  >
    <Typography sx={{ fontSize: '0.85rem', color: TF_SUBTEXT, fontWeight: 500 }}>{step}</Typography>
    <Typography sx={{ fontSize: '0.85rem', color: TF_SUBTEXT }}>→</Typography>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: TF_TEXT }}>{label}</Typography>
  </Box>
);

// ── Bottom nav bar ────────────────────────────────────────────────────────────
function BottomBar({ onPrev, onNext, onOk, showPrev, isLast, isSubmitting, label = 'OK' }) {
  return (
    <Box
      sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
        backgroundColor: '#fff',
        borderTop: '1px solid #e8e8e8',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1,
      }}
    >
      {/* Left: nav arrows (desktop) or back chevron (mobile) */}
      <Box display="flex" gap={0.5}>
        {showPrev && (
          <Box
            onClick={onPrev}
            sx={{
              width: 36, height: 36, borderRadius: 1,
              backgroundColor: TF_BTN, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', '&:hover': { backgroundColor: TF_BTN_HOVER },
            }}
          >
            <KeyboardArrowUp sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
        )}
        <Box
          onClick={onNext}
          sx={{
            width: 36, height: 36, borderRadius: 1,
            backgroundColor: TF_BTN, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', '&:hover': { backgroundColor: TF_BTN_HOVER },
          }}
        >
          <KeyboardArrowDown sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
      </Box>

      {/* Center: OK / Submit button */}
      <Box display="flex" alignItems="center">
        <TFButton onClick={onOk} disabled={isSubmitting} sx={{ px: 4 }}>
          {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : label}
        </TFButton>
        {!isLast && <EnterHint />}
      </Box>

      {/* Right: Powered by Backrr */}
      <Typography
        sx={{
          fontSize: '0.75rem', color: TF_SUBTEXT,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        Powered by <Box component="span" fontWeight={700} color={TF_TEXT}>Backrr</Box>
      </Typography>
    </Box>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────
const STEPS = [
  'Contact Information', 'Location', 'Startup Profile',
  'Solution Overview', "Founder's Profile", 'Investment',
];
const TOTAL_STEPS = STEPS.length;

const emptyForm = {
  first_name: '', last_name: '', email: '', mobile_number: '', phone_code: '91',
  country: null, state: null, city: null,
  startup_name: '', website_url: '', linkedin_url: '',
  stage: null, sector: null, category: null, business_model: [], formation_date: '',
  problem_solving: '', description: '',
  founder_first_name: '', founder_last_name: '', founder_bio: '', founder_linkedin_url: '',
  pitch_deck_file: null, round_name: '', round_type: null, total_round_size: '', currency: null,
};

// ── Yup schemas per step ──────────────────────────────────────────────────────
const stepSchemas = {
  1: Yup.object({
    first_name: Yup.string().trim().required('First name is required').matches(/^[^0-9]*$/, 'First name cannot contain numbers'),
    last_name: Yup.string().trim().required('Last name is required').matches(/^[^0-9]*$/, 'Last name cannot contain numbers'),
    email: Yup.string().trim().email('Invalid email address').required('Email is required'),
    mobile_number: Yup.string("Enter Mobile Number")
      .min(5, 'Mobile number must be at least 5 characters long')
      .max(15, 'Mobile number cannot be more than 15 characters long').required("Mobile number is required"),
  }),
  2: Yup.object({
    country: Yup.object().nullable().required('Country is required'),
    state: Yup.object().nullable().required('State is required'),
    city: Yup.object().nullable().required('City is required'),
  }),
  3: Yup.object({
    startup_name: Yup.string().trim().required('Startup name is required'),
    website_url: Yup.string().trim().required('Website URL is required'),
    linkedin_url: Yup.string().trim().required('LinkedIn URL is required'),
    stage: Yup.object().nullable().required('Startup stage is required'),
    sector: Yup.object().nullable().required('Sector is required'),
    category: Yup.object().nullable().required('Category is required'),
    business_model: Yup.array().min(1, 'Business model is required'),
    formation_date: Yup.string().trim().required('Formation Date is required'),
  }),
  4: Yup.object({
    problem_solving: Yup.string("Enter problem statement")
      .required("Problem statement is required")
      .trim(),
    description: Yup.string().required('Business overview is required')
      .test(
        'richText-not-empty',
        'Business overview is required',
        value => {
          // Check if the value is empty or just a new line
          const strippedValue = value.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, '').replace(/\s+/g, '').trim();
          return strippedValue !== '';
        }
      ).test(
        'richText-length',
        'Business overview must be between 1 and 2000 characters',
        value => {
          const strippedValue = value.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, '').replace(/\s+/g, '').trim();
          return strippedValue.length >= 1 && strippedValue.length <= 2000;
        }
      )
  }),
  5: Yup.object({
    founder_first_name: Yup.string().trim().required('Founder first name is required').matches(/^[^0-9]*$/, 'First name cannot contain numbers'),
    founder_last_name: Yup.string().trim().required('Founder last name is required').matches(/^[^0-9]*$/, 'Last name cannot contain numbers'),
    founder_bio: Yup.string().trim().required('Founder bio is required'),
    founder_linkedin_url: Yup.string().trim().required('Founder LinkedIn URL is required'),
  }),
  6: Yup.object({
    pitch_deck_file: Yup.mixed()
      .required('Pitch deck is required')
      .nonNullable('Pitch deck is required')
      .test(
        'file-size',
        'Pitch deck must be smaller than 50MB',
        (value) => {
          if (!value) return false;
          return (value).size <= 50 * 1024 * 1024;
        }
      )
      .test(
        'file-type',
        'Only PDF files are allowed',
        (value) => {
          if (!value) return false;
          return (value).type === 'application/pdf';
        }
      ),
  }).test('investment', '', function (value) {
    const { round_name, round_type, total_round_size } = value;
    const any = round_name || round_type || total_round_size;
    if (!any) return true; // all optional if none filled
    const errs = {};
    if (!round_name) errs.round_name = 'Round name is required';
    if (!round_type) errs.round_type = 'Round type is required';
    if (!total_round_size) errs.total_round_size = 'Total round size is required';
    if (Object.keys(errs).length) {
      return this.createError({ message: JSON.stringify(errs) });
    }
    return true;
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
export default function ApplyForm() {
  const { slug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: brandingData, isLoading: isBrandingLoading } = FetchApplyFormInfo({ slug });
  const { mutateAsync: submitForm, isLoading: isSubmitting } = SubmitApplyForm();
  const { mutateAsync: sendOtpMutation, isLoading: isSendingOtp } = SendApplyFormOtp();
  const { mutateAsync: verifyOtpMutation, isLoading: isVerifyingOtp } = VerifyApplyFormOtp();

  const [step, setStep] = useState(0);        // 0 = splash
  const [phase, setPhase] = useState('form'); // 'form' | 'otp' | 'startup-exists'
  const [entering, setEntering] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formError, setFormError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [existingStartup, setExistingStartup] = useState(null);

  const formik = useFormik({
    initialValues: emptyForm,
    validateOnChange: true,
    validateOnBlur: false,
    validateOnMount: false,
    enableReinitialize: false,
    onSubmit: () => { }, // handled manually per-step
  });

  const { values, errors, touched, setFieldValue, setFieldTouched, setTouched, setFieldError } = formik;

  // Validate a single field against the current step's schema on blur
  const validateField = async (fieldName) => {
    const schema = stepSchemas[step];
    if (!schema?.fields?.[fieldName]) return;
    try {
      await schema.fields[fieldName].validate(values[fieldName]);
      formik.setFieldError(fieldName, undefined);
    } catch (e) {
      formik.setFieldError(fieldName, e.message);
    }
  };

  const handleBlur = (fieldName) => {
    setFieldTouched(fieldName, true, false);
    validateField(fieldName);
  };

  // ── Dropdown data via hooks ───────────────────────────────────────────────
  const { data: countriesData = [] } = FetchAllCountries();
  const { data: phoneCodesData = [] } = FetchAllCountriesWithPhoneCodes({ search: '' });
  const { data: statesData = [] } = FetchAllStates({ countryID: values.country?.value });
  const { data: citiesData = [] } = FetchAllCities({ stateID: values.state?.value });
  const { data: stagesData = [] } = FetchAllStages();
  const { data: categoriesData = [] } = FetchAllCategories();
  console.log(values, "value");
  const { data: sectorsData = [] } = FetchAllSubCategories({ category: values?.category?.label ?  [values?.category?.label] : [] });
  const { data: businessModelsData = [] } = FetchAllBusinessModel();
  const { data: roundTypesData = [] } = FetchAllRoundTypes();
  const { data: currenciesData = [] } = FetchAllCurrencyLists();

  // Map to consistent {label, value, _id} shape for TFDropdown
  const countries = countriesData;                      // already {label, value, _id}
  const states = statesData;                            // already {label, value, _id}
  const cities = citiesData;                            // already {label, value, _id}
  const stages = toOpts(stagesData);
  const sectors = toOpts(sectorsData);
  const categories = toOpts(categoriesData);
  const businessModels = toOpts(businessModelsData);
  const roundTypes = toOpts(roundTypesData);
  const currencies = toOpts(currenciesData);
  const countryCodes = (phoneCodesData || []).map((c) => ({
    label: c.label || c.name || '',
    value: c.code || c.phone_code || '',
    _id: c._id || c.value || '',
  }));

  // Reset state/city when country changes
  const prevCountryId = useRef(null);
  useEffect(() => {
    if (values.country?._id && values.country._id !== prevCountryId.current) {
      prevCountryId.current = values.country._id;
      setFieldValue('state', null);
      setFieldValue('city', null);
    }
  }, [values.country?._id]);

  // Reset city when state changes
  const prevStateId = useRef(null);
  useEffect(() => {
    if (values.state?._id && values.state._id !== prevStateId.current) {
      prevStateId.current = values.state._id;
      setFieldValue('city', null);
    }
  }, [values.state?._id]);

  const prevCategoryId = useRef(null);
  useEffect(() => {
    if (values.category?._id && values.category._id !== prevCategoryId.current) {
      prevCategoryId.current = values.category._id;
      setFieldValue('sector', null);
    }
  }, [values.category?._id]);


  // Enter key → advance (not during OTP — OTP handles its own Enter per-input)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Enter' && phase === 'form' && step >= 1 && step < TOTAL_STEPS) handleNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Validate current step and touch all its fields to surface errors
  const stepFields = {
    1: ['first_name', 'last_name', 'email', 'mobile_number'],
    2: ['country', 'state', 'city'],
    3: ['startup_name', 'website_url', 'linkedin_url', 'stage', 'sector', 'category', 'business_model', 'formation_date'],
    4: ['problem_solving', 'description'],
    5: ['founder_first_name', 'founder_last_name', 'founder_bio', 'founder_linkedin_url'],
    6: ['round_name', 'round_type', 'total_round_size'],
  };

  const validateStep = async () => {
    const schema = stepSchemas[step];
    if (!schema) return true;

    const fields = stepFields[step] || [];
    // Mark every field on this step as touched so errors become visible
    const touchMap = fields.reduce((acc, f) => ({ ...acc, [f]: true }), {});

    try {
      await schema.validate(values, { abortEarly: false });
      setTouched({ ...touched, ...touchMap }, false);
      return true;
    } catch (yupErr) {
      // Extract per-field errors from Yup ValidationError
      const fieldErrors = {};
      if (yupErr.inner?.length > 0) {
        yupErr.inner.forEach((e) => { if (e.path) fieldErrors[e.path] = e.message; });
      } else if (yupErr.path) {
        fieldErrors[yupErr.path] = yupErr.message;
      }
      // Step 6 uses a custom .test() that packs errors into the message as JSON
      if (step === 6 && !yupErr.inner?.length && yupErr.message) {
        try {
          Object.assign(fieldErrors, JSON.parse(yupErr.message));
        } catch (_) { }
      }
      // Push touched + errors into formik together so they render in one pass
      formik.setErrors({ ...formik.errors, ...fieldErrors });
      setTouched({ ...touched, ...touchMap }, false);
      return false;
    }
  };

  const handleEnter = () => {
    setEntering(true);
    setTimeout(() => { setStep(1); setEntering(false); }, 300);
  };

  const handleNext = async () => {
    const valid = await validateStep();
    if (!valid) {
      scrollToFirstError(); // ✅
      return;
    }
    setFormError('');
    if (step === 1) {
      try {
        await sendOtpMutation({ email: values.email.toLowerCase().trim() });
        setOtpCode('');
        setOtpError('');
        setPhase('otp');
      } catch (err) {
        setFormError(err?.response?.data?.message || 'Failed to send OTP. Please try again.');
      }
      return;
    }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    scrollToTop();
  };

  const handlePrev = () => {
    setFormError('');
    if (phase === 'otp') { setPhase('form'); return; }
    setStep((s) => Math.max(s - 1, 1));
    scrollToTop();
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 4) { setOtpError('Please enter the 4-digit OTP.'); return; }
    try {
      const res = await verifyOtpMutation({ email: values.email.toLowerCase().trim(), otp: otpCode, slug });
      const data = res?.data?.data;
      const isValidOTP = res.data?.statusCode === 200;
      const alreadyInPipeline = res.data?.statusCode === 409

      if(alreadyInPipeline) {
        setSubmitStatus("duplicate");
        setPhase('startup-exists');
      }
      else if (isValidOTP) {
        if (data?.startup?._id) {
          setExistingStartup(data.startup);
          setPhase('startup-exists');
        } else {
          setPhase('form');
          setStep(2);
        }
        scrollToTop();
      }
      else {
        setOtpError(res?.data?.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setOtpError(err?.response?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  const handleDirectApply = async () => {
    const fd = new FormData();
    fd.append('email', values.email.toLowerCase().trim());
    try {
      const res = await submitForm({ slug, payload: fd });
      const statusCode = res?.data?.statusCode;
      if (statusCode === 409) setSubmitStatus('duplicate');
      else if (statusCode === 200) setSubmitStatus('success');
      else setFormError(res?.data?.message || 'Submission failed.');
    } catch (err) {
      setFormError('Submission failed. Please try again.');
    }
  };

  const handleSubmit = async () => {
    const valid = await validateStep();
    if (!valid) {
      scrollToFirstError(); // ✅
      return;
    }
    const fd = new FormData();
    fd.append('first_name', values.first_name);
    fd.append('last_name', values.last_name);
    fd.append('email', values.email.toLowerCase().trim());
    fd.append('mobile_number', values.mobile_number);
    fd.append('phone_code', values.phone_code);
    if (values.country?._id) fd.append('country_id', values.country._id);
    if (values.state?._id) fd.append('state_id', values.state._id);
    if (values.city?._id) fd.append('city_id', values.city._id);
    fd.append('startup_name', values.startup_name);
    fd.append('website_url', values.website_url);
    fd.append('linkedin_url', values.linkedin_url);
    if (values.stage?._id) fd.append('stage_id', values.stage._id);
    if (values.sector?._id) fd.append('sector_id', values.sector._id);
    if (values.category?._id) fd.append('category_id', values.category._id);
    if (Array.isArray(values.business_model) && values.business_model.length > 0) {
      values.business_model.forEach((bm) => fd.append('business_model_id', bm._id));
    }
    if (values.formation_date) fd.append('formation_date', values.formation_date);
    fd.append('problem_solving', values.problem_solving);
    fd.append('description', values.description);
    fd.append('founder_first_name', values.founder_first_name);
    fd.append('founder_last_name', values.founder_last_name);
    fd.append('founder_bio', values.founder_bio);
    fd.append('founder_linkedin_url', values.founder_linkedin_url);
    if (values.pitch_deck_file) fd.append('pitch_deck', values.pitch_deck_file);
    if (values.round_name) fd.append('round_name', values.round_name);
    if (values.round_type?._id) fd.append('round_type', values.round_type._id);
    if (values.total_round_size) fd.append('total_round_size', values.total_round_size);
    if (values.currency?._id) fd.append('currency_id', values.currency._id);
    try {
      const res = await submitForm({ slug, payload: fd });
      const statusCode = res?.data?.statusCode;
      if (statusCode === 409) setSubmitStatus('duplicate');
      else if (statusCode === 200) setSubmitStatus('success');
      else setFormError(res?.data?.message || 'Submission failed.');
    } catch (err) {
      setFormError('Submission failed. Please try again.');
    }
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (isBrandingLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100dvh" bgcolor={TF_BG}>
        <CircularProgress sx={{ color: TF_BTN }} />
      </Box>
    );
  }

  if (!brandingData || brandingData?.statusCode === 404 || brandingData?.data === null) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100dvh" gap={2}>
        <Typography variant="h5" fontWeight={700}>Investor Not Found</Typography>
        <Typography color="#777">The link you followed does not exist or has been removed.</Typography>
      </Box>
    );
  }

  const { organization_name, logo } = brandingData?.data || {};

  if (submitStatus === 'success' || submitStatus === 'duplicate') {
    return <StatusScreen orgName={organization_name} logo={logo} isDuplicate={submitStatus === 'duplicate'} />;
  }

  const progress = step === 0 ? 0 : Math.round((step / TOTAL_STEPS) * 100);

  // ── Splash screen (step 0) ────────────────────────────────────────────────
  if (step === 0) {
    return (
      <SplashScreen
        logo={logo}
        orgName={organization_name}
        onEnter={handleEnter}
        exiting={entering}
      />
    );
  }

  // ── OTP verification screen ───────────────────────────────────────────────
  if (phase === 'otp') {
    return (
      <Box sx={{ minHeight: '100dvh', backgroundColor: TF_BG, display: 'flex', flexDirection: 'column', pb: 10 }}>
        <LinearProgress variant="determinate" value={Math.round((1 / TOTAL_STEPS) * 100)}
          sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 300, height: 3, backgroundColor: '#e8e8e8', '& .MuiLinearProgress-bar': { backgroundColor: TF_PROGRESS } }} />
        <Box sx={{ mt: '3px' }}>
          <StepHeader step="OTP" label="Email Verification" />
        </Box>
        <Box sx={{ flex: 1, maxWidth: { xs: '100%', sm: 640, md: 720 }, mx: 'auto', width: { xs: 'calc(100% - 3rem)', sm: "calc(100% - 6rem)" }, px: { xs: 3, sm: 6, md: 8 }, pt: { xs: 4, sm: 6 }, animation: `${fadeUp} 0.4s ease both` }}>
          <StepOtp
            email={values.email}
            otpCode={otpCode}
            setOtpCode={setOtpCode}
            otpError={otpError}
            setOtpError={setOtpError}
            onVerify={handleVerifyOtp}
            isVerifying={isVerifyingOtp}
            onResend={async () => {
              setOtpError('');
              try { await sendOtpMutation({ email: values.email.toLowerCase().trim() }); }
              catch (e) { setOtpError('Failed to resend OTP.'); }
            }}
            isSending={isSendingOtp}
          />
        </Box>
        <BottomBar
          showPrev
          onPrev={handlePrev}
          onNext={handleVerifyOtp}
          onOk={handleVerifyOtp}
          isLast={false}
          isSubmitting={isVerifyingOtp}
          label="Verify"
        />
      </Box>
    );
  }

  // ── Startup already exists screen ─────────────────────────────────────────
  if (phase === 'startup-exists') {
    return (
      <StartupExistsScreen
        logo={logo}
        orgName={organization_name}
        startup={existingStartup}
        email={values.email}
        onApply={handleDirectApply}
        isSubmitting={isSubmitting}
        error={formError}
      />
    );
  }

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            minHeight: '100dvh', backgroundColor: TF_BG,
            display: 'flex', flexDirection: 'column',
            pb: 10,  // space for fixed bottom bar
          }}
        >
          {/* Top progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              position: 'fixed', top: 0, left: 0, right: 0, zIndex: 300,
              height: 3, backgroundColor: '#e8e8e8',
              '& .MuiLinearProgress-bar': { backgroundColor: TF_PROGRESS, transition: 'transform 0.4s ease' },
            }}
          />

          {/* Step header */}
          <Box sx={{ mt: '3px' }}>
            <StepHeader step={step} label={STEPS[step - 1]} />
          </Box>

          {/* Content area */}
          <Box
            sx={{
              flex: 1,
              maxWidth: { xs: '100%', sm: 640, md: 720 },
              mx: 'auto', width: { xs: 'calc(100% - 3rem)', sm: "calc(100% - 6rem)" },
              px: { xs: 3, sm: 6, md: 8 },
              pt: { xs: 4, sm: 6 },
              animation: `${fadeUp} 0.4s ease both`,
            }}
          >
            {step === 1 && <StepContact values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} handleBlur={handleBlur} countryCodes={countryCodes} />}
            {step === 2 && <StepLocation values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} handleBlur={handleBlur} setFieldTouched={setFieldTouched} countries={countries} states={states} cities={cities} />}
            {step === 3 && <StepStartupProfile values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} handleBlur={handleBlur} setFieldTouched={setFieldTouched} stages={stages} sectors={sectors} categories={categories} businessModels={businessModels} />}
            {step === 4 && <StepOverview values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} handleBlur={handleBlur} />}
            {step === 5 && <StepFounder values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} handleBlur={handleBlur} />}
            {step === 6 && <StepInvestment values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} handleBlur={handleBlur} setFieldTouched={setFieldTouched} roundTypes={roundTypes} currencies={currencies} setFieldError={setFieldError} />}

            {formError && <ErrText msg={formError} />}
          </Box>

          {/* Bottom navigation bar */}
          <BottomBar
            showPrev={step > 1}
            onPrev={handlePrev}
            onNext={handleNext}
            onOk={step < TOTAL_STEPS ? handleNext : handleSubmit}
            isLast={step === TOTAL_STEPS}
            isSubmitting={isSubmitting || isSendingOtp}
            label={step === 1 ? 'Send OTP' : step === TOTAL_STEPS ? 'Submit' : 'OK'}
          />
        </Box>
      </Form>
    </FormikProvider>
  );
}

// ── Splash screen ─────────────────────────────────────────────────────────────
function SplashScreen({ logo, orgName, onEnter, exiting }) {
  const [showBtn, setShowBtn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowBtn(true), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Enter') onEnter();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onEnter]);

  return (
    <Box
      sx={{
        minHeight: '100dvh', backgroundColor: TF_BG,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 0, px: 3,
        opacity: exiting ? 0 : 1,
        transition: 'opacity 0.3s ease',
      }}
    >
      {/* Logo */}
      <Box
        display="flex" alignItems="center" gap={2}
        sx={{ mb: 1.5, animation: `${scaleIn} 0.6s cubic-bezier(0.34,1.56,0.64,1) both`, animationDelay: '0.1s' }}
      >
        {logo
          ? <Box component="img" src={logo} alt={orgName} sx={{ height: 56, maxWidth: 180, objectFit: 'contain', borderRadius: 1 }} />
          : <InitialsAvatar name={orgName} size={56} />
        }
      </Box>

      {/* Org name — sits close under logo */}
      {orgName && (
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, 
        // color: '#e05c28',
         letterSpacing: '-0.5px', mb: 2 }}>
          {orgName}
        </Typography>
      )}

      {/* Hello Founder */}
      <Typography
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.8rem' }, fontWeight: 500,
          color: TF_TEXT, textAlign: 'center', lineHeight: 1.2,
          mb: 0.75,
          animation: `${fadeUp} 0.55s ease both`, animationDelay: '0.45s',
          opacity: 0, animationFillMode: 'forwards',
        }}
      >
        Hello Founder!
      </Typography>

      {/* Tagline — tight to heading */}
      <Typography
        sx={{
          fontSize: '1rem', color: TF_SUBTEXT, textAlign: 'center', maxWidth: 340,
          lineHeight: 1.4, mb: 2.5,
          animation: `${fadeUp} 0.55s ease both`, animationDelay: '0.65s',
          opacity: 0, animationFillMode: 'forwards',
        }}
      >
        Tell us what you're building.
      </Typography>

      {/* Get Started button — controlled distance from tagline */}
      <Box
        sx={{
          opacity: showBtn ? 1 : 0,
          transform: showBtn ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 0.45s ease, transform 0.45s ease',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}
      >
        <TFButton onClick={onEnter} sx={{ px: 4, py: 1.3, fontSize: '1rem' }}>
          Get Started
        </TFButton>
        <EnterHint />
      </Box>

      {/* Progress strip */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, backgroundColor: '#f0f0f0' }}>
        <Box sx={{ height: '100%', backgroundColor: TF_PROGRESS, animation: `${progressFill} 5s linear both` }} />
      </Box>
    </Box>
  );
}

// ── Step 1: Contact Information ───────────────────────────────────────────────
function StepContact({ values, errors, touched, setFieldValue, handleBlur, countryCodes }) {
  const err = (f) => touched[f] && errors[f];
  return (
    <>
      <FieldWrap>
        <TFLabel>First Name *</TFLabel>
        <TFInput
          placeholder="Type your answer here…"
          value={values.first_name}
          onChange={(e) => setFieldValue('first_name', capitalizeFirst(sanitizePersonName(e.target.value)))}
          onBlur={() => handleBlur('first_name')}
          error={!!err('first_name')}
          autoFocus
        />
        <ErrText msg={err('first_name')} />
      </FieldWrap>

      <FieldWrap>
        <TFLabel>Last Name *</TFLabel>
        <TFInput
          placeholder="Type your answer here…"
          value={values.last_name}
          onChange={(e) => setFieldValue('last_name', capitalizeFirst(sanitizePersonName(e.target.value)))}
          onBlur={() => handleBlur('last_name')}
          error={!!err('last_name')}
        />
        <ErrText msg={err('last_name')} />
      </FieldWrap>

      <FieldWrap>
        <TFLabel>Email Address *</TFLabel>
        <TFInput
          type="email"
          placeholder="name@example.com"
          value={values.email}
          onChange={(e) => setFieldValue('email', e.target.value.toLowerCase())}
          onBlur={() => handleBlur('email')}
          error={!!err('email')}
        />
        <ErrText msg={err('email')} />
      </FieldWrap>

      <FieldWrap>
        <PhoneField
          countryCodes={countryCodes}
          selectedCode={values.phone_code}
          onCodeChange={(e) => setFieldValue('phone_code', e.target.value)}
          value={values.mobile_number}
          onChange={(e) => setFieldValue('mobile_number', e.target.value)}
          onBlur={() => handleBlur('mobile_number')}
          error={!!err('mobile_number')}
          errorMsg={err('mobile_number')}
        />
      </FieldWrap>
    </>
  );
}

// ── Step 2: Location ──────────────────────────────────────────────────────────
function StepLocation({ values, errors, touched, setFieldValue, handleBlur, countries, states, cities }) {
  const err = (f) => touched[f] && errors[f];
  const selectField = (f) => (v) => { setFieldValue(f, v); handleBlur(f); };
  const countryRef = useRef(null); // ✅

  // Auto-focus country dropdown on mount
  useEffect(() => {
    const t = setTimeout(() => countryRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <FieldWrap>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem', color: TF_SUBTEXT }}>a.</Typography>
          <Box flex={1}>
            <TFDropdown
              focusRef={countryRef}
              label="Country of Operation *"
              placeholder="Type or select an option"
              options={countries}
              value={values.country}
              onSelect={selectField('country')}
              error={!!err('country')}
              errorMsg={err('country')}
              autoFocus={true}
            />
          </Box>
        </Box>
      </FieldWrap>
      <FieldWrap>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem', color: TF_SUBTEXT }}>b.</Typography>
          <Box flex={1}>
            <TFDropdown
              label="State *"
              placeholder="Type or select an option"
              options={states}
              value={values.state}
              onSelect={selectField('state')}
              error={!!err('state')}
              errorMsg={err('state')}
            />
          </Box>
        </Box>
      </FieldWrap>
      <FieldWrap>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem', color: TF_SUBTEXT }}>c.</Typography>
          <Box flex={1}>
            <TFDropdown
              label="City *"
              placeholder="Type or select an option"
              options={cities}
              value={values.city}
              onSelect={selectField('city')}
              error={!!err('city')}
              errorMsg={err('city')}
            />
          </Box>
        </Box>
      </FieldWrap>
    </>
  );
}

// ── Step 3: Startup Profile ───────────────────────────────────────────────────
function StepStartupProfile({ values, errors, touched, setFieldValue, handleBlur, stages, sectors, categories, businessModels }) {
  const err = (f) => touched[f] && errors[f];
  const sf = (f) => (v) => { setFieldValue(f, v); handleBlur(f); };
  return (
    <>
      <FieldWrap>
        <TFLabel>Startup Name *</TFLabel>
        <TFInput placeholder="Type your answer here…" value={values.startup_name}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 30 && /^[^@$()]*$/.test(value)) {
              setFieldValue('startup_name', value.charAt(0).toUpperCase() + value.slice(1));
            }
          }}
          onBlur={() => handleBlur('startup_name')}
          error={!!err('startup_name')}
          autoFocus
        />
        <ErrText msg={err('startup_name')} />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Startup Website *</TFLabel>
        <TFInput placeholder="https://" value={values.website_url}
          onChange={(e) => setFieldValue('website_url', e.target.value)}
          onBlur={() => handleBlur('website_url')}
          error={!!err('website_url')} />
        <ErrText msg={err('website_url')} />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Company LinkedIn *</TFLabel>
        <TFInput placeholder="https://" value={values.linkedin_url}
          onChange={(e) => setFieldValue('linkedin_url', e.target.value)}
          onBlur={() => handleBlur('linkedin_url')}
          error={!!err('linkedin_url')} />
        <ErrText msg={err('linkedin_url')} />
      </FieldWrap>
      <FieldWrap>
        <TFDropdown label="Startup Stage *" placeholder="Type or select an option" options={stages} value={values.stage} onSelect={sf('stage')} error={!!err('stage')} errorMsg={err('stage')} />
      </FieldWrap>
      <FieldWrap>
        <TFDropdown label="Category *" placeholder="Type or select an option" options={categories} value={values.category} onSelect={sf('category')} error={!!err('category')} errorMsg={err('category')} />
      </FieldWrap>
      <FieldWrap>
        <TFDropdown label="Sector *" placeholder="Type or select an option" options={sectors} value={values.sector} onSelect={sf('sector')} error={!!err('sector')} errorMsg={err('sector')} />
      </FieldWrap>
      <FieldWrap>
        <TFDropdown label="Business Model *" placeholder="Type or select an option" options={businessModels} value={values.business_model} onSelect={sf('business_model')} error={!!err('business_model')} errorMsg={err('business_model')} multiple />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Formation Date *</TFLabel>
        <TFInput placeholder="e.g. 2020" value={values.formation_date}
          onChange={(e) => setFieldValue('formation_date', e.target.value)}
          onBlur={() => handleBlur('formation_date')}
          error={!!err('formation_date')}
          type='date'
        />
        <ErrText msg={err('formation_date')} />
      </FieldWrap>
    </>
  );
}

// ── Step 4: Startup Overview ──────────────────────────────────────────────────
function StepOverview({ values, errors, touched, setFieldValue, handleBlur }) {
  const err = (f) => touched[f] && errors[f];
  return (
    <>
      <FieldWrap>
        <TFLabel>Describe the core problem your startup solves *</TFLabel>
        <TFInput multiline minRows={2} placeholder="Enter a description…"
          value={values.problem_solving}
          onChange={(e) => setFieldValue('problem_solving', e.target.value.substring(0, 160))}
          onBlur={() => handleBlur('problem_solving')}
          error={!!err('problem_solving')}
          autoFocus
        />
        <ErrText msg={err('problem_solving')} />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Business Overview &amp; Solution *</TFLabel>
        <TFInput multiline minRows={2} placeholder="Enter business overview…"
          value={values.description}
          onChange={(e) => setFieldValue('description', e.target.value.substring(0, 2000))}
          onBlur={() => handleBlur('description')}
          error={!!err('description')}
        />
        <ErrText msg={err('description')} />
      </FieldWrap>
    </>
  );
}

// ── Step 5: Founder's Profile ─────────────────────────────────────────────────
function StepFounder({ values, errors, touched, setFieldValue, handleBlur }) {
  const err = (f) => touched[f] && errors[f];
  return (
    <>
      <FieldWrap>
        <TFLabel>Founder First Name *</TFLabel>
        <TFInput placeholder="Type your answer here…" value={values.founder_first_name}
          onChange={(e) => setFieldValue('founder_first_name', capitalizeFirst(sanitizePersonName(e.target.value)))}
          onBlur={() => handleBlur('founder_first_name')}
          error={!!err('founder_first_name')} autoFocus />
        <ErrText msg={err('founder_first_name')} />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Founder Last Name *</TFLabel>
        <TFInput placeholder="Type your answer here…" value={values.founder_last_name}
          onChange={(e) => setFieldValue('founder_last_name', capitalizeFirst(sanitizePersonName(e.target.value)))}
          onBlur={() => handleBlur('founder_last_name')}
          error={!!err('founder_last_name')}
        />
        <ErrText msg={err('founder_last_name')} />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Founder Bio *</TFLabel>
        <TFInput multiline minRows={2} placeholder="Type your answer here…" value={values.founder_bio}
          onChange={(e) => setFieldValue('founder_bio', e.target.value)}
          onBlur={() => handleBlur('founder_bio')}
          error={!!err('founder_bio')} />
        <ErrText msg={err('founder_bio')} />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Founder LinkedIn Profile *</TFLabel>
        <TFInput placeholder="https://" value={values.founder_linkedin_url}
          onChange={(e) => setFieldValue('founder_linkedin_url', e.target.value)}
          onBlur={() => handleBlur('founder_linkedin_url')}
          error={!!err('founder_linkedin_url')} />
        <ErrText msg={err('founder_linkedin_url')} />
      </FieldWrap>
    </>
  );
}

// ── Step 6: Investment ────────────────────────────────────────────────────────
function StepInvestment({ values, errors, touched, setFieldValue, handleBlur, setFieldTouched, roundTypes, currencies, setFieldError }) {
  const fileInputRef = useRef(null);
  const err = (f) => touched[f] && errors[f];
  const sf = (f) => (v) => { setFieldValue(f, v); handleBlur(f); };
  return (
    <>
      {/* Pitch deck upload */}
      <FieldWrap>
        <TFLabel>Pitch Deck Upload</TFLabel>
        <Box
          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          sx={{
            border: '2px dashed #d0d0d0', borderRadius: 2, p: 4,
            textAlign: 'center', cursor: 'pointer', backgroundColor: '#f7f7f7',
            '&:hover': { borderColor: TF_BTN, backgroundColor: '#f0f0f0' },
          }}
        >
          <CloudUpload sx={{ fontSize: 48, color: '#c0c0c0', mb: 1 }} />
          <Typography color={TF_TEXT} fontWeight={600}>
            {values.pitch_deck_file ? values.pitch_deck_file.name : 'Choose file'}
          </Typography>
          <Typography variant="caption" color={TF_SUBTEXT}>Size limit: 50MB</Typography>
          <input
            ref={fileInputRef} type="file" accept=".pdf,.ppt,.pptx"
            style={{ display: 'none' }}
            onClick={(e) => { e.target.value = ''; }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setFieldTouched('pitch_deck_file')
              setFieldValue('pitch_deck_file', file);
            }}
          />
        </Box>
        <ErrText msg={err('pitch_deck_file')} />
        {/* <Typography variant="caption" color={TF_SUBTEXT} mt={0.5} display="block">
          Shared only with the investment team.
        </Typography> */}
      </FieldWrap>

      <FieldWrap>
        <TFLabel>Round Name (Optional)</TFLabel>
        <TFInput placeholder="e.g. Pre-Seed" value={values.round_name}
          onChange={(e) => setFieldValue('round_name', e.target.value)}
          onBlur={() => handleBlur('round_name')}
          error={!!err('round_name')} />
        <ErrText msg={err('round_name')} />
      </FieldWrap>
      <FieldWrap>
        <TFDropdown label="Round Type (Optional)" placeholder="Type or select an option"
          options={roundTypes} value={values.round_type} onSelect={sf('round_type')}
          error={!!err('round_type')} errorMsg={err('round_type')} />
      </FieldWrap>
      <FieldWrap>
        <TFDropdown label="Currency (Optional)" placeholder="Type or select an option"
          options={currencies} value={values.currency} onSelect={sf('currency')} />
      </FieldWrap>
      <FieldWrap>
        <TFLabel>Total Round Size (Optional)</TFLabel>
        <TFInput placeholder="e.g. 500000" value={values.total_round_size}
          onChange={(e) => setFieldValue('total_round_size', e.target.value)}
          onBlur={() => handleBlur('total_round_size')}
          error={!!err('total_round_size')}
          inputProps={currencyInputProps}
        />
        <ErrText msg={err('total_round_size')} />
      </FieldWrap>

    </>
  );
}

// ── OTP Step ──────────────────────────────────────────────────────────────────
function StepOtp({ email, otpCode, setOtpCode, otpError, setOtpError, onVerify, isVerifying, onResend, isSending }) {
  const inputsRef = useRef([]);

  const handleChange = (index, val) => {
    const digit = val.replace(/\D/, '');
    const chars = otpCode.split('');
    chars[index] = digit;
    const next = chars.join('');
    setOtpCode(next);
    setOtpError('');
    if (digit && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') onVerify();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      setOtpCode(pasted.padEnd(6, '').slice(0, 6));
      setOtpError('');
      inputsRef.current[Math.min(pasted.length, 5)]?.focus();
      e.preventDefault();
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: TF_TEXT, mb: 1 }}>
        Verify your email
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', color: TF_SUBTEXT, mb: 4 }}>
        We sent a 4-digit code to <Box component="span" fontWeight={600} color={TF_TEXT}>{email}</Box>. Enter it below.
      </Typography>

      <Box display="flex" gap={1.5} mb={1.5} onPaste={handlePaste}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Box
            key={i}
            component="input"
            inputRef={(el) => (inputsRef.current[i] = el)}
            ref={(el) => (inputsRef.current[i] = el)}
            value={otpCode[i] || ''}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            maxLength={1}
            inputMode="numeric"
            autoFocus={i === 0}
            sx={{
              width: 48, height: 56, textAlign: 'center', fontSize: '1.4rem', fontWeight: 600,
              border: `2px solid ${otpError ? '#e53e3e' : otpCode[i] ? TF_ACTIVE : TF_BORDER}`,
              borderRadius: 1, outline: 'none', color: TF_TEXT, backgroundColor: TF_BG,
              transition: 'border-color 0.15s',
              '&:focus': { borderColor: TF_ACTIVE },
            }}
          />
        ))}
      </Box>

      {otpError && <ErrText msg={otpError} />}

      <Box display="flex" alignItems="center" gap={1} mt={3}>
        <Typography sx={{ fontSize: '0.85rem', color: TF_SUBTEXT }}>Didn't receive the code?</Typography>
        <Typography
          onClick={!isSending ? onResend : undefined}
          sx={{ fontSize: '0.85rem', fontWeight: 600, color: isSending ? TF_SUBTEXT : TF_BTN, cursor: isSending ? 'default' : 'pointer', textDecoration: 'underline' }}
        >
          {isSending ? 'Sending…' : 'Resend OTP'}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Startup Exists Screen ─────────────────────────────────────────────────────
function StartupExistsScreen({ logo, orgName, startup, email, onApply, isSubmitting, error }) {
  return (
    <Box sx={{ minHeight: '100dvh', backgroundColor: TF_BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, px: 3, textAlign: 'center' }}>
      {logo
        ? <Box component="img" src={logo} alt={orgName} sx={{ height: 64, objectFit: 'contain', animation: `${scaleIn} 0.5s ease both` }} />
        : orgName && <InitialsAvatar name={orgName} size={64} />}

      <Box sx={{ animation: `${fadeUp} 0.4s ease both` }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: TF_TEXT, mb: 3 }}>
          Welcome back!
        </Typography>
        {startup?.startup_name && (
          <Typography sx={{ fontSize: '1rem', color: TF_SUBTEXT, mb: 0.5 }}>
            <Box component="span" fontWeight={600} color={TF_TEXT}>{startup.startup_name}</Box> is already registered.
          </Typography>
        )}
        <Typography sx={{ fontSize: '0.9rem', color: TF_SUBTEXT, maxWidth: 360, mx: 'auto', mb: 4 }}>
          Click below to apply
        </Typography>

        <TFButton onClick={onApply} disabled={isSubmitting} sx={{ px: 5, py: 1.4, fontSize: '1rem' }}>
          {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Apply Now'}
        </TFButton>

        {error && <ErrText msg={error} />}
      </Box>
    </Box>
  );
}

// ── Status screen ─────────────────────────────────────────────────────────────
function StatusScreen({ logo, orgName, isDuplicate }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
      minHeight="100dvh" gap={3} px={3} sx={{ backgroundColor: TF_BG, textAlign: 'center' }}>
      {logo
        ? <Box component="img" src={logo} alt={orgName} sx={{ height: 56, objectFit: 'contain', animation: `${scaleIn} 0.5s ease both`, borderRadius: 1 }} />
        : orgName && <InitialsAvatar name={orgName} size={56} />}
      <Typography fontWeight={700} fontSize="1.4rem" color={TF_TEXT}>Thank You!</Typography>
      <Typography color={TF_SUBTEXT} maxWidth={400} lineHeight={1.7}>
        {isDuplicate
          ? 'Application already received and will be reviewed by our investment team.'
          : 'Your application has been received and will be reviewed by our investment team.'}
      </Typography>
    </Box>
  );
}